import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 14;
export const UPPERCASE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const LOWERCASE_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
export const DIGITS = '0123456789';
export const SPECIAL_CHARACTERS = '!@#$%^&*()_-+[]{}|;:,.<>?';

export function generateStrongPassword(length = getRandomPasswordLength()): string {
    if (length < 4) {
        throw new Error('Password length must be at least 4');
    }
    const allCharacters = UPPERCASE_LETTERS + LOWERCASE_LETTERS + DIGITS + SPECIAL_CHARACTERS;

    const getRandomChar = (chars: string): string => {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return chars[array[0] % chars.length];
    };

    let password = '';

    password += getRandomChar(UPPERCASE_LETTERS);
    password += getRandomChar(LOWERCASE_LETTERS);
    password += getRandomChar(DIGITS);
    password += getRandomChar(SPECIAL_CHARACTERS);

    for (let i = 4; i < length; i++) {
        password += getRandomChar(allCharacters);
    }

    const chars = password.split('');
    for (let i = chars.length - 1; i > 0; i--) {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        const j = array[0] % (i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    return chars.join('');
}

export function getRandomPasswordLength(minLength = MIN_PASSWORD_LENGTH, maxLength = MAX_PASSWORD_LENGTH): number {
    const range = maxLength - minLength + 1;
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return (array[0] % range) + minLength;
}

export function passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) {
            return null;
        }
        const errors: ValidationErrors = {};
        if (value.length < MIN_PASSWORD_LENGTH || value.length > MAX_PASSWORD_LENGTH) {
            errors.length = {requiredLength: `${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH}`, actualLength: value.length};
        }
        if (!/[A-Z]/.test(value)) {
            errors.uppercase = true;
        }
        if (!/[a-z]/.test(value)) {
            errors.lowercase = true;
        }
        if (!/[0-9]/.test(value)) {
            errors.digit = true;
        }
        if (!/[!@#$%^&*()_\-+\[\]{}|;:,.<>?]/.test(value)) {
            errors.specialChar = true;
        }
        return Object.keys(errors).length ? errors : null;
    };
}

export function passwordsMatchValidator(passwordControlName: string, confirmPasswordControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const passwordControl = formGroup.get(passwordControlName);
        const confirmPasswordControl = formGroup.get(confirmPasswordControlName);
        if (!passwordControl || !confirmPasswordControl) {
            confirmPasswordControl?.setErrors({passwordsMismatch: true});
            return {passwordsMismatch: true};
        }
        if (passwordControl.value !== confirmPasswordControl.value) {
            confirmPasswordControl.setErrors({passwordsMismatch: true});
            return {passwordsMismatch: true};
        } else {
            confirmPasswordControl.setErrors(null);
            return null;
        }
    };
}
